import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["onClick"];
import React, { Fragment, useCallback } from 'react';
import { useIntl } from 'react-intl-next';
import Button from '@atlaskit/button';
import ThumbUpIcon from '@atlaskit/icon/core/thumbs-up';
import { ThumbUpIcon as LegacyThumbUpIcon } from '@atlaskit/legacy-custom-icons';
import { N500 } from '@atlaskit/theme/colors';
import Tooltip from '@atlaskit/tooltip';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import { messages } from '../../messages';
import { useAIAnswerContext } from '../ai-answer-context';
import { onDislikeButtonClick, onLikeButtonClick } from '../analytics';
import { FeedbackButtonIconWrapper, Rotate180IconWrapper } from './styled';
var FeedbackButton = function FeedbackButton(_ref) {
  var active = _ref.active,
    isDisabled = _ref.isDisabled,
    onClickHandler = _ref.onClick,
    tooltipContent = _ref.tooltipContent,
    testId = _ref.testId,
    icon = _ref.icon,
    isReadingAids = _ref.isReadingAids;
  return /*#__PURE__*/React.createElement(Tooltip, {
    position: 'top',
    content: tooltipContent
  }, function (_ref2) {
    var onClick = _ref2.onClick,
      tooltipProps = _objectWithoutProperties(_ref2, _excluded);
    return /*#__PURE__*/React.createElement(Button, _extends({
      "aria-label": tooltipContent,
      spacing: 'none',
      appearance: isDisabled ? 'subtle-link' : 'subtle',
      iconBefore: /*#__PURE__*/React.createElement(FeedbackButtonIconWrapper, _extends({
        active: active
        // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
        ,
        style: {
          cursor: 'pointer'
        }
      }, testId && {
        'data-testid': testId
      }, {
        isDisabled: isDisabled || false,
        isReadingAids: isReadingAids || false
      }), icon)
    }, !isDisabled && {
      onClick: onClickHandler
    }, tooltipProps));
  });
};
export var FeedbackButtons = function FeedbackButtons(_ref3) {
  var setDisplayFlag = _ref3.setDisplayFlag,
    setFeedbackModal = _ref3.setFeedbackModal;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var _useAIAnswerContext = useAIAnswerContext(),
    commonAttributes = _useAIAnswerContext.commonAttributes,
    fireAnalyticsEvent = _useAIAnswerContext.fireAnalyticsEvent,
    updateContextCache = _useAIAnswerContext.updateContextCache,
    liked = _useAIAnswerContext.liked,
    setLiked = _useAIAnswerContext.setLiked,
    likesDisabled = _useAIAnswerContext.likesDisabled,
    setLikesDisabled = _useAIAnswerContext.setLikesDisabled,
    isReadingAids = _useAIAnswerContext.isReadingAids;
  var _useAIEventsInstrumen = useAIEventsInstrumentation(),
    trackAIFeedbackSubmit = _useAIEventsInstrumen.trackAIFeedbackSubmit;
  // Using a single state to represent both like/dislike states
  // true - liked, false - disliked, undefined - neither are selected
  var likeButtonCallback = useCallback(function (e) {
    e.preventDefault();
    setDisplayFlag(true);
    setLiked(true);
    setLikesDisabled(true);
    updateContextCache({
      collapsed: false,
      liked: true
    });
    fireAnalyticsEvent(onLikeButtonClick(commonAttributes));
    trackAIFeedbackSubmit('up');
  }, [setLiked, setLikesDisabled, fireAnalyticsEvent, commonAttributes, updateContextCache, setDisplayFlag, trackAIFeedbackSubmit]);
  var dislikeButtonCallback = useCallback(function (e) {
    e.preventDefault();
    setFeedbackModal(true);
    setLiked(false);
    setLikesDisabled(true);
    updateContextCache({
      collapsed: false,
      liked: false
    });
    fireAnalyticsEvent(onDislikeButtonClick(commonAttributes));
    trackAIFeedbackSubmit('down');
  }, [setLiked, setLikesDisabled, fireAnalyticsEvent, commonAttributes, updateContextCache, setFeedbackModal, trackAIFeedbackSubmit]);
  return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(FeedbackButton, {
    active: liked === true,
    onClick: likeButtonCallback,
    tooltipContent: formatMessage(messages.ai_answer_component_like_button_tooltip),
    icon: /*#__PURE__*/React.createElement(ThumbUpIcon, {
      label: "",
      spacing: "spacious",
      color: "var(--ds-icon, ".concat(N500, ")"),
      LEGACY_fallbackIcon: LegacyThumbUpIcon
    }),
    isDisabled: liked && likesDisabled,
    testId: "ai-answer-like-button",
    isReadingAids: isReadingAids
  }), /*#__PURE__*/React.createElement(FeedbackButton, {
    active: liked === false,
    onClick: dislikeButtonCallback,
    tooltipContent: formatMessage(messages.ai_answer_component_dislike_button_tooltip),
    icon: /*#__PURE__*/React.createElement(Rotate180IconWrapper, null, /*#__PURE__*/React.createElement(ThumbUpIcon, {
      label: "",
      spacing: "spacious",
      color: "var(--ds-icon, ".concat(N500, ")"),
      LEGACY_fallbackIcon: LegacyThumbUpIcon
    })),
    isDisabled: !liked && likesDisabled,
    testId: "ai-answer-dislike-button",
    isReadingAids: isReadingAids
  }));
};